import React, { useState } from "react";
import { Button } from "aible";
import { getBaseCurrentAlias, isBaseProjection } from "types/expression";
import * as ExpressionUtils from "utils/expression";
import * as RelationUtils from "utils/relation";
import { AliasedProjectionInput, ExcludableProjectionInput } from "./ExpressionInput";
import SearchInputDelayed from "components/Inputs/SearchInput/SearchInputDelayed";
import styles from "components/Tiles/Sense/style.module.css";
const ProjectRelationInput = ({ relation, onChange, isDisabled }) => {
    const aliasFormatter = RelationUtils.getAliasFormatter(relation);
    const context = RelationUtils.getOutputContext(relation.input);
    const inputColumns = ExpressionUtils.getSchemaColumns(context);
    const [searchVal, updateSearchVal] = useState("");
    const hasSearch = searchVal?.length > 0;
    const searchedColumns = hasSearch
        ? inputColumns.filter((col) => col.toLowerCase()?.includes(searchVal.toLowerCase()))
        : inputColumns;
    const inputColumnSet = new Set(inputColumns);
    const baseProjectionsByCurrentAlias = {};
    const newProjections = [];
    for (const expr of relation.project) {
        if (isBaseProjection(expr)) {
            const currentAlias = getBaseCurrentAlias(expr);
            if (inputColumnSet.has(currentAlias)) {
                baseProjectionsByCurrentAlias[currentAlias] = expr;
                continue;
            }
        }
        newProjections.push(expr);
    }
    const handleChange = (exprs) => {
        onChange(RelationUtils.builder(relation.input).project(exprs).build());
    };
    const currentBaseSelect = inputColumns
        .map((alias) => baseProjectionsByCurrentAlias[alias])
        .filter((expr) => expr !== undefined);
    const currentOtherSelected = inputColumns
        .filter((item) => !item?.toLowerCase()?.includes(searchVal?.toLowerCase()))
        .map((alias) => baseProjectionsByCurrentAlias[alias])
        .filter((expr) => expr !== undefined);
    const isSomeSelected = inputColumns.some((c) => {
        return baseProjectionsByCurrentAlias[c] === undefined;
    });
    const someSearchSelected = searchedColumns.some((c) => {
        return baseProjectionsByCurrentAlias[c] === undefined;
    });
    const handleSelectAll = () => {
        const isExcluded = !isSomeSelected;
        const retColumns = inputColumns
            .map((a) => (isExcluded ? undefined : (baseProjectionsByCurrentAlias[a] ?? a)))
            .filter((e) => e !== undefined).concat(newProjections);
        handleChange(retColumns);
    };
    const handleSelectAllSearched = () => {
        const retColumns = searchedColumns
            .map((a) => {
            return !someSearchSelected ? undefined : (baseProjectionsByCurrentAlias[a] ?? a);
        })
            .filter((e) => e !== undefined)
            .concat(currentOtherSelected)
            .concat(newProjections);
        if (retColumns?.length === inputColumns?.length) {
            const defaultColumns = inputColumns.map((a) => baseProjectionsByCurrentAlias[a] ?? a);
            return handleChange(defaultColumns);
        }
        return handleChange(retColumns);
    };
    const displayInputColumns = [...searchedColumns].sort();
    return (React.createElement("div", { className: styles.modifyFields },
        React.createElement("div", { className: styles.projectRelationActions },
            inputColumns.length > 0 && (React.createElement("div", { className: styles.modifyFieldsLabel },
                React.createElement("div", { className: styles.jBold + " " + styles.jLabel }, "Filter"),
                React.createElement(Button, { isSmall: true, style: { marginLeft: "10px" }, onClick: !hasSearch ? handleSelectAll : handleSelectAllSearched, disabled: !(searchedColumns?.length > 0) }, hasSearch
                    ? someSearchSelected
                        ? "Include Subset"
                        : "Filter Out Subset"
                    : isSomeSelected
                        ? "Include All"
                        : "Filter Out All"))),
            React.createElement(SearchInputDelayed, { value: searchVal, onChange: (val) => {
                    updateSearchVal(val);
                }, inputClass: styles.projectRelationSearch, delay: 750, responsive: true })),
        displayInputColumns.map((alias, i) => {
            const expr = baseProjectionsByCurrentAlias[alias] ?? alias;
            const isExcluded = !(alias in baseProjectionsByCurrentAlias);
            return (React.createElement("div", { key: alias, style: {
                    fontSize: "12px",
                    marginTop: i > 0 ? "10px" : undefined,
                    paddingBottom: i == displayInputColumns.length - 1 ? "10px" : undefined
                } },
                React.createElement(ExcludableProjectionInput, { context: context, expr: expr, onChange: (x) => handleChange(currentBaseSelect.map((e) => getBaseCurrentAlias(e) == alias ? x : e).concat(newProjections)), isExcluded: isExcluded, onExcludedChange: (excluded) => handleChange(inputColumns
                        .map((a) => (alias != a ? baseProjectionsByCurrentAlias[a] : excluded ? undefined : a))
                        .filter((e) => e !== undefined).concat(newProjections)), isDisabled: isDisabled, aliasFormatter: aliasFormatter })));
        }),
        newProjections.map((expr, index) => (React.createElement(AliasedProjectionInput, { key: index, context: context, expr: expr, onChange: (x) => handleChange(currentBaseSelect.concat(newProjections.map((e, i) => (i == index ? x : e)))), isDisabled: isDisabled, aliasFormatter: aliasFormatter })))));
};
export default ProjectRelationInput;
